import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaSeedling,
  FaWarehouse,
  FaBox,
  FaChevronDown,
  FaFilter,
} from "react-icons/fa";
import "./style.css";

// --- Transaction Card Component (same as ProductTransactions) ---
const TransactionCard = ({
  movement,
  productName,
  productUnit,
  fieldsMap,
  locationsMap,
}) => {
  const { t, i18n } = useTranslation();
  const isStockIn = movement.type === "in";

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t("Today");
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t("Yesterday");
    }

    return date.toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(i18n.language === "ar" ? "ar-EG" : "en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get simplified transaction type
  const getTransactionType = (movement) => {
    const movementType = movement.movementType?.toLowerCase();

    if (movementType) {
      switch (movementType) {
        case "load":
          return t("Stock Added");
        case "usage":
          return movement.fieldId ? t("Field Usage") : t("Used");
        case "allocate":
        case "transfer":
          return t("Transfer");
        case "other":
          return t("Adjustment");
        default:
          return isStockIn ? t("Added") : t("Used");
      }
    }

    // Fallback logic
    if (movement.fieldId) return t("Field Usage");
    if (movement.transferPaired) return t("Transfer");
    return isStockIn ? t("Stock In") : t("Stock Out");
  };

  // Get location/field context
  const getContext = (movement) => {
    // For field usage, show the field name
    if (movement.fieldId) {
      if (fieldsMap && fieldsMap[movement.fieldId]) {
        return fieldsMap[movement.fieldId];
      }
      return `${t("Field")} #${movement.fieldId}`;
    }

    // For transfers, show the destination/source
    if (movement.transferPaired) {
      if (
        movement.type === "out" &&
        movement.destinationLocationId &&
        locationsMap[movement.destinationLocationId]
      ) {
        return `${t("To")}: ${locationsMap[movement.destinationLocationId]}`;
      }
      if (
        movement.type === "in" &&
        movement.sourceLocationId &&
        locationsMap[movement.sourceLocationId]
      ) {
        return `${t("From")}: ${locationsMap[movement.sourceLocationId]}`;
      }
    }

    // For other movements with location
    if (movement.locationId && locationsMap && locationsMap[movement.locationId]) {
      return locationsMap[movement.locationId];
    }

    return null;
  };

  return (
    <div
      className={`transaction-card-simple ${
        isStockIn ? "stock-in" : "stock-out"
      }`}
    >
      {/* Movement Direction Indicator */}
      <div className={`direction-indicator ${isStockIn ? "in" : "out"}`}>
        {isStockIn ? <FaArrowUp /> : <FaArrowDown />}
      </div>

      {/* Main Content Area */}
      <div className="transaction-content">
        {/* Header Row */}
        <div className="transaction-row header-row">
          <div className="left-section">
            <span className="transaction-type">
              {getTransactionType(movement)}
            </span>
            {getContext(movement) && (
              <span className="transaction-context">
                • {getContext(movement)}
              </span>
            )}
          </div>
          <div className="right-section">
            <span className="transaction-date">
              {formatDate(movement.date)}
            </span>
            <span className="transaction-time">
              {formatTime(movement.date)}
            </span>
          </div>
        </div>

        {/* Product Info Row */}
        <div className="transaction-row product-info-row">
          <div className="product-name-display">
            <FaBox className="product-icon" />
            <span>{productName}</span>
          </div>
        </div>

        {/* Details Row */}
        <div className="transaction-row details-row">
          <div className="left-section">
            <span className={`quantity ${isStockIn ? "positive" : "negative"}`}>
              {isStockIn ? "+" : "-"}
              {movement.quantity} {productUnit}
            </span>
            {movement.price && (
              <span className="price-info">@ ${movement.price}/unit</span>
            )}
          </div>
        </div>

        {/* Notes if present */}
        {movement.movementNotes && (
          <div className="transaction-row notes-row">
            <span className="notes-text">{movement.movementNotes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Usage Component ---
const Usage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);
  
  // Data state
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState({});
  const [fieldsMap, setFieldsMap] = useState({});
  const [locationsMap, setLocationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterOption, setFilterOption] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);
  
  // Statistics
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const [activeProducts, setActiveProducts] = useState(0);

  const farmId = userData.Employee?.farmId;
  const tenantId = userData.Employee?.tenantId;

  // Fetch all data with retry mechanism
  const fetchData = useCallback(async (retryCount = 0) => {
      if (!farmId || !token) return;
      
      // Only show loading on first attempt
      if (retryCount === 0) {
        setLoading(true);
      }
      
      try {
        // Fetch inventory movements
        const movementsRes = await axios.get(
          `api/inventoryMovements/findByFarmId/${farmId}`,
          { headers: { Authorization: token } }
        );

        // Better handling of empty responses
        if (!movementsRes.data.items || movementsRes.data.items.length === 0 || 
            !movementsRes.data.items[0]?.InventoryMovements) {
          
          // If we just added a movement and get empty response, retry
          if (retryCount < 2 && location.state?.movementAdded) {
            const delay = 1000 * (retryCount + 1);
            console.log(`Empty response after adding movement, retrying in ${delay}ms...`);
            setTimeout(() => fetchData(retryCount + 1), delay);
            return;
          }
          
          setMovements([]);
          setTotalIn(0);
          setTotalOut(0);
          setActiveProducts(0);
          setLoading(false);
          return;
        }

        const rawMovements = movementsRes.data.items[0]?.InventoryMovements || {
          Procurements: [],
          Distributions: [],
        };

        // Process procurements and distributions
        const allMovements = [];
        const productMap = {};
        const fieldIds = new Set();
        const locationIds = new Set();

        // Process Procurements
        if (rawMovements.Procurements) {
          rawMovements.Procurements.forEach((item) => {
            const procurement = item.Procurement || item;
            const product = item.InventoryItemDetails || {};

            const movement = {
              id: procurement.id,
              type: "in",
              date: procurement.procurementDate,
              quantity: procurement.quantity || item.quantity,
              price: product.price || 0, // Get price from InventoryItemDetails
              movementType: procurement.movementType || "load",
              movementNotes: procurement.movementNotes || "",
              locationId: procurement.locationId,
              productId: product.id || procurement.inventoryItemDetailId,
              fieldId: procurement.fieldId,
            };

            allMovements.push(movement);

            // Store product info with price
            if (product.id) {
              productMap[product.id] = {
                nameAr: product.nameAr,
                nameEn: product.nameEn,
                unit: product.unit || t("unit"),
                price: product.price || 0, // Store price in product map
              };
            }

            if (procurement.fieldId) fieldIds.add(procurement.fieldId);
            if (procurement.locationId) locationIds.add(procurement.locationId);
          });
        }

        // Process Distributions
        if (rawMovements.Distributions) {
          rawMovements.Distributions.forEach((item) => {
            const distribution = item.Distribution || item;
            const product = item.InventoryItemDetails || {};

            const movement = {
              id: distribution.id,
              type: "out",
              date: distribution.distributionDate,
              quantity: distribution.quantity || item.quantity,
              price: product.price || 0, // Get price from InventoryItemDetails (will be 0 for distributions)
              movementType: distribution.movementType || "usage",
              movementNotes: distribution.movementNotes || "",
              locationId: distribution.locationId,
              productId: product.id || distribution.inventoryItemDetailId,
              fieldId: distribution.fieldId || distribution.field_id,
            };

            allMovements.push(movement);

            // Store product info with price
            if (product.id) {
              productMap[product.id] = {
                nameAr: product.nameAr,
                nameEn: product.nameEn,
                unit: product.unit || t("unit"),
                price: product.price || 0, // Store price in product map
              };
            }

            if (distribution.fieldId || distribution.field_id) {
              fieldIds.add(distribution.fieldId || distribution.field_id);
            }
            if (distribution.locationId) locationIds.add(distribution.locationId);
          });
        }

        // Fetch field names
        const fieldNamesMap = {};
        if (fieldIds.size > 0) {
          try {
            const fieldPromises = Array.from(fieldIds).map(async (fieldId) => {
              try {
                const fieldRes = await axios.get(`api/field/${fieldId}`, {
                  headers: { Authorization: token },
                });
                const field = fieldRes.data.items?.[0];
                if (field) {
                  fieldNamesMap[fieldId] = field.name || `Field ${fieldId}`;
                }
              } catch (err) {
                console.error(`Failed to fetch field ${fieldId}:`, err);
                fieldNamesMap[fieldId] = `Field ${fieldId}`;
              }
            });
            await Promise.all(fieldPromises);
          } catch (err) {
            console.error("Error fetching field names:", err);
          }
        }
        setFieldsMap(fieldNamesMap);

        // Fetch location names
        const locationNamesMap = {};
        if (locationIds.size > 0) {
          try {
            const locationPromises = Array.from(locationIds).map(
              async (locationId) => {
                try {
                  const locationRes = await axios.get(
                    `api/inventoryLocation/${locationId}`,
                    { headers: { Authorization: token } }
                  );
                  const location = locationRes.data.items?.[0];
                  if (location) {
                    locationNamesMap[locationId] =
                      i18n.language === "ar"
                        ? location.nameAr || location.nameEn || `Location ${locationId}`
                        : location.nameEn || location.nameAr || `Location ${locationId}`;
                  }
                } catch (err) {
                  console.error(`Failed to fetch location ${locationId}:`, err);
                  locationNamesMap[locationId] = `Location ${locationId}`;
                }
              }
            );
            await Promise.all(locationPromises);
          } catch (err) {
            console.error("Error fetching location names:", err);
          }
        }
        setLocationsMap(locationNamesMap);

        // Detect paired transfers
        const processedMovements = [];
        const pairedIndices = new Set();

        allMovements.forEach((move, index) => {
          if (pairedIndices.has(index)) return;

          const timestamp = new Date(move.date).getTime();
          const pairedIndex = allMovements.findIndex(
            (m, i) =>
              i !== index &&
              !pairedIndices.has(i) &&
              Math.abs(new Date(m.date).getTime() - timestamp) < 1000 &&
              m.type !== move.type &&
              m.quantity === move.quantity &&
              m.productId === move.productId
          );

          if (pairedIndex !== -1) {
            const paired = allMovements[pairedIndex];
            pairedIndices.add(index);
            pairedIndices.add(pairedIndex);

            move.transferPaired = true;
            paired.transferPaired = true;
            
            if (move.type === "in") {
              move.sourceLocationId = paired.locationId;
              paired.destinationLocationId = move.locationId;
            } else {
              move.destinationLocationId = paired.locationId;
              paired.sourceLocationId = move.locationId;
            }
            
            processedMovements.push(move);
            processedMovements.push(paired);
          } else {
            processedMovements.push(move);
          }
        });

        // Sort by date (newest first)
        processedMovements.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setMovements(processedMovements);
        setProducts(productMap);
        
        // Calculate statistics
        const totalInQty = processedMovements
          .filter(m => m.type === "in")
          .reduce((sum, m) => sum + (m.quantity || 0), 0);
        
        const totalOutQty = processedMovements
          .filter(m => m.type === "out")
          .reduce((sum, m) => sum + (m.quantity || 0), 0);
        
        const uniqueProducts = new Set(processedMovements.map(m => m.productId).filter(id => id));
        
        setTotalIn(totalInQty);
        setTotalOut(totalOutQty);
        setActiveProducts(uniqueProducts.size);
        
      } catch (error) {
        console.error("Failed to fetch movements:", error);
        
        // Retry on network errors
        if (retryCount < 2 && (error.code === 'ECONNABORTED' || !error.response)) {
          const delay = 1000 * (retryCount + 1);
          console.log(`Network error, retrying in ${delay}ms...`);
          setTimeout(() => fetchData(retryCount + 1), delay);
          return;
        }
        
        toast.error(t("Failed to load inventory movements."));
      } finally {
        if (retryCount === 0 || retryCount >= 2) {
          setLoading(false);
        }
      }
  }, [farmId, token, t, i18n.language, location.state]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle refresh on navigation return and window focus
  useEffect(() => {
    // Check if we're returning from AddMovement with refresh flag
    if (location.state?.refresh) {
      fetchData();
      // Clear the state to prevent repeated refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Refresh on window focus (like Android's onResume)
  useEffect(() => {
    const handleFocus = () => {
      // Refresh data when window regains focus
      fetchData();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Handle filter dropdown outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper function to get display name
  const getDisplayName = (item, keyAr = "nameAr", keyEn = "nameEn") => {
    if (!item) return "";
    return i18n.language === "ar" ? item[keyAr] : item[keyEn] || item[keyAr];
  };

  // Get product display name
  const getProductName = (productId) => {
    const product = products[productId];
    if (!product) return `Product #${productId}`;
    return getDisplayName(product);
  };

  // Get product unit
  const getProductUnit = (productId) => {
    const product = products[productId];
    return product?.unit || t("unit");
  };

  // Filter movements based on selected filter
  const filteredMovements = movements.filter((movement) => {
    if (filterOption === "all") return true;
    if (filterOption === "in") return movement.type === "in";
    if (filterOption === "out") return movement.type === "out";
    if (filterOption === "transfers") return movement.transferPaired;
    return true;
  });

  const filterOptionsList = [
    { key: "all", label: t("All") },
    { key: "in", label: t("Stock In") },
    { key: "out", label: t("Stock Out") },
    { key: "transfers", label: t("Transfers") },
  ];

  if (loading) {
    return (
      <div className="usage-container">
        <div className="loading-spinner">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="usage-container">
      {/* Header */}
      <header className="view-header">
        <div className="header-left-content">
          <div>
            <h1 className="view-header-title">{t("Inventory Movements")}</h1>
            <p className="view-header-subtitle">
              {movements.length} {t("total movements")}
            </p>
          </div>
        </div>
        <button
          className="button-primary"
          onClick={() => navigate("/inventory/usage/add")}
        >
          <FaPlus /> {t("Add Movement")}
        </button>
      </header>

      {/* Summary Stats Section */}
      <div className="transaction-summary">
        <div className="summary-card total-in">
          <div className="summary-label">{t("Total Stock In")}</div>
          <div className="summary-value">
            +{totalIn.toFixed(2)}
          </div>
        </div>
        <div className="summary-card total-out">
          <div className="summary-label">{t("Total Stock Out")}</div>
          <div className="summary-value">
            -{totalOut.toFixed(2)}
          </div>
        </div>
        <div className="summary-card active-products">
          <div className="summary-label">{t("Active Products")}</div>
          <div className="summary-value">
            {activeProducts}
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="content-toolbar">
        <div className="filter-dropdown-wrapper" ref={filterRef}>
          <button
            className="filter-dropdown-button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <FaFilter className="filter-icon" />
            {t("Filter")}:{" "}
            <span>
              {filterOptionsList.find((opt) => opt.key === filterOption)?.label}
            </span>
            <FaChevronDown
              className={`chevron-icon ${isFilterOpen ? "open" : ""}`}
            />
          </button>
          {isFilterOpen && (
            <div className="filter-dropdown-menu">
              {filterOptionsList.map((option) => (
                <div
                  key={option.key}
                  className="filter-dropdown-item"
                  onClick={() => {
                    setFilterOption(option.key);
                    setIsFilterOpen(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transactions List */}
      <div className="transactions-list">
        {filteredMovements.length > 0 ? (
          <>
            <h2 className="section-title">{t("Movement History")}</h2>
            {filteredMovements.map((movement) => (
              <TransactionCard
                key={`${movement.type}-${movement.id}-${movement.date}`}
                movement={movement}
                productName={getProductName(movement.productId)}
                productUnit={getProductUnit(movement.productId)}
                fieldsMap={fieldsMap}
                locationsMap={locationsMap}
              />
            ))}
          </>
        ) : (
          <div className="no-data-message">
            <FaBox className="empty-icon" />
            <p>{t("No movements found.")}</p>
            <p className="help-text">
              {t("Add your first movement to get started.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Usage;
